import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useUser } from "../context/UserContext";
import Sidebar from "../components/Sidebar";
import PostViewerModal from "../components/PostViewerModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserCircle } from "@fortawesome/free-regular-svg-icons";
import "../styles/postPage.css";

export default function PostPage() {
    const { postId } = useParams();
    const navigate = useNavigate();
    const { user } = useUser();

    const [post, setPost] = useState(null);
    const [postProfile, setPostProfile] = useState(null);
    const [authorPosts, setAuthorPosts] = useState([]); // all posts by same author for prev/next in modal
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);

    useEffect(() => {
        if (!postId) return;
        loadPost();
    }, [postId, user]);

    const loadPost = async () => {
        setLoading(true);
        setNotFound(false);

        // 1. Fetch the post with images + likes
        const { data: postData, error } = await supabase
            .from("posts")
            .select(`
                id, caption, created_at, hide_like_counts, turn_off_commenting, user_id,
                profiles:user_id(id, username, avatar_url, is_private),
                post_images(id, image_path, order_index),
                likes(user_id)
            `)
            .eq("id", postId)
            .single();

        if (error || !postData) {
            setNotFound(true);
            setLoading(false);
            return;
        }

        // 2. Check visibility: if author's account is private, only show if viewer follows them
        const authorProfile = postData.profiles;
        if (authorProfile?.is_private && user?.id !== authorProfile.id) {
            const { data: followData } = await supabase
                .from("follows")
                .select("id")
                .eq("follower_id", user?.id ?? "")
                .eq("following_id", authorProfile.id)
                .maybeSingle();

            if (!followData) {
                // Not following a private account → redirect to their profile page
                navigate(`/user/${encodeURIComponent(authorProfile.username)}`, { replace: true });
                return;
            }
        }

        // 3. Sign image URLs
        const sorted = (postData.post_images || []).sort((a, b) => a.order_index - b.order_index);
        const withUrls = await Promise.all(sorted.map(async (img) => {
            const { data: sd } = await supabase.storage
                .from("posts").createSignedUrl(img.image_path, 3600);
            return { ...img, signedUrl: sd?.signedUrl || null };
        }));

        const formattedPost = {
            ...postData,
            post_images: withUrls,
            likesCount: postData.likes?.length || 0,
            isLiked: postData.likes?.some(l => l.user_id === user?.id) || false,
        };

        // 4. Fetch all posts by the same author (for prev/next in modal)
        const { data: allPosts } = await supabase
            .from("posts")
            .select(`
                id, caption, created_at, user_id,
                post_images(id, image_path, order_index),
                likes(user_id)
            `)
            .eq("user_id", postData.user_id)
            .order("created_at", { ascending: false });

        const formattedAll = await Promise.all((allPosts || []).map(async (p) => {
            const s = (p.post_images || []).sort((a, b) => a.order_index - b.order_index);
            const urls = await Promise.all(s.map(async (img) => {
                const { data: sd } = await supabase.storage
                    .from("posts").createSignedUrl(img.image_path, 3600);
                return { ...img, signedUrl: sd?.signedUrl || null };
            }));
            return {
                ...p,
                post_images: urls,
                likesCount: p.likes?.length || 0,
                isLiked: p.likes?.some(l => l.user_id === user?.id) || false,
            };
        }));

        setPost(formattedPost);
        setPostProfile(authorProfile);
        setAuthorPosts(formattedAll);
        setLoading(false);
        setModalOpen(true);
    };

    const handleClose = () => {
        // On close, go to the author's profile page
        if (postProfile?.username) {
            const isOwn = user?.id === postProfile.id;
            navigate(isOwn ? "/profile" : `/user/${encodeURIComponent(postProfile.username)}`, { replace: true });
        } else {
            navigate(-1);
        }
    };

    const currentIdx = authorPosts.findIndex(p => p.id === post?.id);

    if (loading) {
        return (
            <div className="postpage-layout">
                <Sidebar />
                <div className="postpage-loading">
                    <div className="postpage-spinner" />
                </div>
            </div>
        );
    }

    if (notFound) {
        return (
            <div className="postpage-layout">
                <Sidebar />
                <div className="postpage-notfound">
                    <p className="postpage-notfound-title">Post not found</p>
                    <p className="postpage-notfound-sub">This post may have been deleted or doesn't exist.</p>
                    <button className="postpage-back-btn" onClick={() => navigate(-1)}>Go back</button>
                </div>
            </div>
        );
    }

    return (
        <div className="postpage-layout">
            <Sidebar />

            {/* Background: blurred preview of the post's first image */}
            <div className="postpage-bg">
                {post?.post_images?.[0]?.signedUrl && (
                    <img src={post.post_images[0].signedUrl} className="postpage-bg-img" alt="" />
                )}
                <div className="postpage-bg-overlay" />
            </div>

            {/* Author info hint while modal opens */}
            <div className="postpage-hint">
                {postProfile?.avatar_url ? (
                    <img src={postProfile.avatar_url} className="postpage-hint-avatar" alt="" />
                ) : (
                    <div className="postpage-hint-avatar-fallback">
                        <FontAwesomeIcon icon={faUserCircle} />
                    </div>
                )}
                <span className="postpage-hint-username">{postProfile?.username}</span>
            </div>

            <PostViewerModal
                open={modalOpen}
                onClose={handleClose}
                post={post}
                posts={authorPosts}
                profile={postProfile}
                onNextPost={() => {
                    if (currentIdx < authorPosts.length - 1) {
                        const next = authorPosts[currentIdx + 1];
                        setPost(next);
                        window.history.replaceState(null, "", `/p/${next.id}`);
                    }
                }}
                onPrevPost={() => {
                    if (currentIdx > 0) {
                        const prev = authorPosts[currentIdx - 1];
                        setPost(prev);
                        window.history.replaceState(null, "", `/p/${prev.id}`);
                    }
                }}
                onPostDeleted={() => { handleClose(); }}
                onPostUpdated={(postId, updatedData) => {
                    setPost(prev => prev?.id === postId ? { ...prev, ...updatedData } : prev);
                    setAuthorPosts(prev => prev.map(p => p.id === postId ? { ...p, ...updatedData } : p));
                }}
            />
        </div>
    );
}
